import React, { useState, useEffect } from 'react';
import { useFlags } from 'wsm-common-data';
import PropTypes from 'prop-types';
import mergeObjects from '../utils/mergeObjects';

const catchHandler = (error) => {
	if (error.code !== 'MODULE_NOT_FOUND') {
		throw error;
	}
};

/**
 * This function loads window.DDC.WidgetData['ws-tagging'].mapping with all the projects events and utils for the site.
 * grouping events and utils are merged into the default events and utils
 * if this turns out to be too heavy all we really need is:
 *    [project].event.events updated with {...defaultEventMapping, ...groupingEventMapping}
 *    [project].utils updated with {...defaultUtils, ...groupingUtils}
 * @param {*} mapping
 */
export const loadMapping = async (mapping) => {
	const allPromises = [];

	Object.entries(mapping).forEach(([project, { grouping }]) => {
		allPromises.push(
			Promise.all([
				project,
				import(`../../src/mappings/${project}/event/default`).catch(
					catchHandler
				),
				import(`../../src/mappings/${project}/event/${grouping}`).catch(
					catchHandler
				),
				import(`../../src/utils/${project}/utils`).catch(catchHandler),
				import(`../../src/utils/${project}/${grouping}/utils`).catch(
					catchHandler
				)
			]).then((projectMapping) => projectMapping)
		);
	});

	(await Promise.all(allPromises)).forEach(
		([
			project,
			{ default: defaultEventMapping = {} } = {},
			{ default: groupingEventMapping = {} } = {},
			defaultUtils = {},
			groupingUtils = {}
		]) => {
			window.DDC.WidgetData['ws-tagging'].mapping[project].event =
				mergeObjects(defaultEventMapping, groupingEventMapping);
			window.DDC.WidgetData['ws-tagging'].mapping[project].utils =
				mergeObjects(defaultUtils, groupingUtils);
		}
	);
};

export default function DataMapper({ mapping = {} }) {
	const flags = useFlags();
	const [appIsMounted, setAppIsMounted] = useState(null);

	useEffect(() => {
		(async () => {
			await loadMapping(mapping);

			const { initIfWsTaggingReady } = await import(
				'../utils/dataMapper'
			);
			initIfWsTaggingReady(flags);
			setAppIsMounted(true);
		})();
	}, [mapping, flags]);

	return appIsMounted ? <span data-testid="DataMapper" /> : null; // placholder is so we can grab this during unit testing
}

DataMapper.propTypes = {
	mapping: PropTypes.objectOf(PropTypes.object)
};
